import { useState } from 'react';
import logo from '../../../assets/new-logo.png';

interface SidebarProps {
  currentStep: number;
  onStepChange: (step: number) => void;
  isExported?: boolean;
}

const sections = [
  {
    id: 1,
    name: 'Sitemap',
    icon: 'ri-node-tree',
    description: 'Site structure',
    items: ['Home', 'About', 'Services', 'Products', 'Blog', 'Gallery', 'Contact'],
  },
  {
    id: 2,
    name: 'Content',
    icon: 'ri-article-line',
    description: 'Page content',
    items: ['Home', 'About Us', 'Services', 'Products', 'Blog', 'Gallery', 'Contact Us'],
  },
  {
    id: 3,
    name: 'Design',
    icon: 'ri-palette-line',
    description: 'Templates & theme',
    items: ['Template Gallery', 'Theme Colours', 'Typography', 'Live Preview'],
  },
  {
    id: 4,
    name: 'Download',
    icon: 'ri-download-cloud-line',
    description: 'Output package',
    items: ['Checklist', 'ZIP Archive', 'Files Ready'],
  },
];

export default function WebsiteBuilderSidebar({ currentStep, onStepChange, isExported = false }: SidebarProps) {
  const [expandedSection, setExpandedSection] = useState<number | null>(1);

  const toggleSection = (stepId: number) => {
    if (isExported) {
      if (stepId === 4) {
        onStepChange(4);
      }
      setExpandedSection(expandedSection === stepId ? null : stepId);
      return;
    }
    if (stepId <= currentStep + 1) {
      onStepChange(stepId);
    }
    setExpandedSection(expandedSection === stepId ? null : stepId);
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-screen flex flex-col flex-shrink-0">
      {/* Logo and Brand */}
      <div className="px-6 border-b border-gray-200 dark:border-gray-700 flex items-center h-[88px] flex-shrink-0">
        <img src={logo} alt="Marketing 4SIGHT Logo" className="h-14 w-auto object-contain" />
      </div>

      {isExported && (
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-start gap-3 flex-shrink-0">
          <i className="ri-information-line text-amber-600 mt-0.5 text-lg"></i>
          <div>
            <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider block">Read-Only Mode</span>
            <p className="text-[11px] text-amber-700 leading-tight mt-1">This project is downloaded and locked. Start fresh to build a new one.</p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {sections.map((section) => {
            const isActive = currentStep === section.id;
            const isCompleted = isExported ? false : currentStep > section.id;
            const isClickable = isExported ? section.id === 4 : section.id <= currentStep + 1;
            const isExpanded = expandedSection === section.id;

            return (
              <div key={section.id}>
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all text-left cursor-pointer ${
                    isActive
                      ? 'primary_bg-color text-white shadow-sm'
                      : isCompleted
                      ? 'bg-[#fdf8e8] text-gray-700 hover:bg-[#f6e7b0]'
                      : isClickable
                      ? 'text-gray-600 hover:bg-gray-50'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-5 h-5 flex items-center justify-center ${isActive ? 'text-white' : isCompleted ? 'text-[#b7791f]' : 'text-gray-400'}`}>
                      <i className={`${section.icon} text-base`}></i>
                    </div>
                    <div className="min-w-0">
                      <span className={`text-sm font-medium block ${isActive ? 'text-white' : ''}`}>{section.name}</span>
                      <span className={`text-[10px] block ${isActive ? 'text-white/60' : 'text-gray-400'}`}>{section.description}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isCompleted && (
                      <i className="ri-check-line text-[#b7791f] text-xs"></i>
                    )}
                    {isClickable && (
                      <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs ${isActive ? 'text-white/60' : 'text-gray-400'}`}></i>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </nav>

      {/* Help Section */}
      <div className="p-3 border-t border-gray-100">
        <div className="bg-[#fdf8e8] rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <i className="ri-question-line text-[#b7791f] text-sm"></i>
            <span className="text-xs font-medium text-gray-700">Need Help?</span>
          </div>
          <p className="text-[11px] text-[#b7791f]/80 leading-relaxed mb-2">
            Follow the wizard steps to build your site.
          </p>
          <button className="text-[11px] text-[#b7791f] hover:text-[#975a16] font-medium cursor-pointer">
            View Tutorial →
          </button>
        </div>
      </div>
    </div>
  );
}